import { useState, useEffect } from 'react';
import { Button } from '@/components/Button';
import type { Collection, CreateCollectionInput, UpdateCollectionInput } from '@maker-toolchest/shared';
import { getRandomCollectionColor, MAX_COLLECTION_DEPTH } from '@maker-toolchest/shared';
import { useCreateCollection, useUpdateCollection, useCollectionTree } from '../hooks';
import { CollectionIconPicker } from './CollectionIconPicker';
import { CollectionColorPicker } from './CollectionColorPicker';
import { useUIStore } from '@/stores/ui';
import { ChevronDown } from 'lucide-react';

interface CreateCollectionFormProps {
  collection?: Collection; // If provided, edit mode
  defaultParentId?: string; // Default parent for new collection
  parentCollectionName?: string; // Name of parent collection for title
  onSuccess?: (collection?: Collection) => void; // Called after successful create/update
  onCancel?: () => void; // Called when user cancels
}

export function CreateCollectionForm({
  collection,
  defaultParentId,
  parentCollectionName: _parentCollectionName,
  onSuccess,
  onCancel,
}: CreateCollectionFormProps) {
  const isEditMode = !!collection;
  const { closeCollectionSidebar, setAutoExpandCollectionId } = useUIStore();

  const [formData, setFormData] = useState({
    name: collection?.name || '',
    description: collection?.description || '',
    icon: collection?.icon || 'folder',
    color: collection?.color || getRandomCollectionColor(),
    parentId: collection?.parentId || defaultParentId || '',
  });

  const [showParentSelector, setShowParentSelector] = useState(false);

  const { data: collectionTree } = useCollectionTree();
  const createCollection = useCreateCollection();
  const updateCollection = useUpdateCollection();
  const { addToast } = useUIStore();

  // Reset form when collection or defaultParentId changes
  useEffect(() => {
    setFormData({
      name: collection?.name || '',
      description: collection?.description || '',
      icon: collection?.icon || 'folder',
      color: collection?.color || getRandomCollectionColor(),
      parentId: collection?.parentId || defaultParentId || '',
    });
  }, [collection, defaultParentId]);

  // Helper to find a collection in tree recursively
  const findCollectionInTree = (node: any, targetId: string): any => {
    if (node.id === targetId) return node;
    if (!node.children) return null;
    for (const child of node.children) {
      const found = findCollectionInTree(child, targetId);
      if (found) return found;
    }
    return null;
  };

  // Calculate subtree depth (max depth from this collection to deepest descendant)
  const getSubtreeDepth = (col: any): number => {
    if (!col.children || col.children.length === 0) {
      return 0;
    }
    let maxChildDepth = 0;
    for (const child of col.children) {
      maxChildDepth = Math.max(maxChildDepth, getSubtreeDepth(child));
    }
    return maxChildDepth + 1;
  };

  // Get subtree depth of current collection (when editing)
  const currentSubtreeDepth = isEditMode && collection
    ? getSubtreeDepth(collectionTree?.find((c: any) => findCollectionInTree(c, collection.id)) || { children: [] })
    : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let result: Collection | undefined;

      if (isEditMode) {
        // Update existing collection
        const updateInput: UpdateCollectionInput = {
          name: formData.name !== collection.name ? formData.name : undefined,
          description: formData.description || undefined,
          icon: formData.icon || undefined,
          color: formData.color || undefined,
          parentId: formData.parentId || undefined,
        };

        result = await updateCollection.mutateAsync({
          id: collection.id,
          input: updateInput,
        });

        addToast({
          type: 'success',
          title: 'Collection updated',
          message: `"${formData.name}" has been updated`,
        });
      } else {
        // Create new collection
        const createInput: CreateCollectionInput = {
          name: formData.name,
          description: formData.description || undefined,
          icon: formData.icon || undefined,
          color: formData.color || undefined,
          parentId: formData.parentId || undefined,
        };

        result = await createCollection.mutateAsync(createInput);

        // If created as a sub-collection, mark parent for auto-expansion
        if (formData.parentId) {
          setAutoExpandCollectionId(formData.parentId);
        }

        addToast({
          type: 'success',
          title: 'Collection created',
          message: `"${formData.name}" has been created`,
        });
      }

      // Only close the global sidebar if we're not in a custom context
      if (!onSuccess) {
        closeCollectionSidebar();
      } else {
        onSuccess(result);
      }
    } catch (error) {
      addToast({
        type: 'error',
        title: isEditMode ? 'Failed to update collection' : 'Failed to create collection',
        message: error instanceof Error ? error.message : 'An error occurred',
      });
    }
  };

  // Flatten tree for parent selector with depth validation
  const flattenTree = (collections: any[], level = 0): any[] => {
    const result: any[] = [];
    for (const col of collections) {
      // Exclude current collection and its descendants when editing
      if (isEditMode && collection && col.id === collection.id) {
        continue;
      }

      // Check if selecting this collection as parent would exceed max depth
      // For new collections: col.depth + 1 must be <= MAX_COLLECTION_DEPTH
      // For editing: col.depth + 1 + currentSubtreeDepth must be <= MAX_COLLECTION_DEPTH
      const wouldExceedDepth = isEditMode
        ? col.depth + 1 + currentSubtreeDepth > MAX_COLLECTION_DEPTH
        : col.depth >= MAX_COLLECTION_DEPTH;

      result.push({ ...col, level, disabled: wouldExceedDepth });
      if (col.children) {
        result.push(...flattenTree(col.children, level + 1));
      }
    }
    return result;
  };

  const flatCollections = flattenTree(collectionTree || []);
  const selectedParent = flatCollections.find((c) => c.id === formData.parentId);

  // Check if "None (Root level)" should be disabled when editing
  // If moving to root, the collection would be at depth 0
  // So the deepest descendant would be at depth 0 + currentSubtreeDepth
  const rootLevelDisabled = isEditMode && currentSubtreeDepth > MAX_COLLECTION_DEPTH;

  const isPending = createCollection.isPending || updateCollection.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            maxLength={100}
            className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Enter collection name"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-foreground mb-2">
            Description
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            maxLength={500}
            className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
            placeholder="Optional description"
          />
        </div>

        {/* Icon & Color */}
        <div className="grid grid-cols-2 gap-4">
          {/* Color Picker */}
          <CollectionColorPicker
            label="Color"
            selectedColor={formData.color}
            onSelect={(color) => setFormData({ ...formData, color })}
          />

          {/* Icon Picker */}
          <CollectionIconPicker
            label="Icon"
            selectedIcon={formData.icon}
            onSelect={(iconName) => setFormData({ ...formData, icon: iconName })}
            color={formData.color}
          />
        </div>

        {/* Parent Collection */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Parent Collection (Optional)
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowParentSelector(!showParentSelector)}
              className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm flex items-center justify-between hover:bg-background-subtle transition-colors"
            >
              <span className="text-foreground-muted">
                {selectedParent ? selectedParent.name : 'None (Root level)'}
              </span>
              <ChevronDown className="h-4 w-4 text-foreground-muted" />
            </button>

            {showParentSelector && (
              <div className="absolute z-10 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-64 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => {
                    if (rootLevelDisabled) return;
                    setFormData({ ...formData, parentId: '' });
                    setShowParentSelector(false);
                  }}
                  disabled={rootLevelDisabled}
                  className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                    rootLevelDisabled
                      ? 'opacity-40 cursor-not-allowed text-foreground-muted'
                      : 'hover:bg-background-subtle'
                  }`}
                  title={rootLevelDisabled ? 'Would exceed maximum nesting depth' : undefined}
                >
                  None (Root level)
                  {rootLevelDisabled && (
                    <span className="ml-2 text-xs text-foreground-muted">(Too deep)</span>
                  )}
                </button>
                {flatCollections.map((col) => (
                  <button
                    key={col.id}
                    type="button"
                    onClick={() => {
                      if (col.disabled) return;
                      setFormData({ ...formData, parentId: col.id });
                      setShowParentSelector(false);
                    }}
                    disabled={col.disabled}
                    className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                      col.disabled
                        ? 'opacity-40 cursor-not-allowed text-foreground-muted'
                        : 'hover:bg-background-subtle'
                    }`}
                    style={{ paddingLeft: `${col.level * 1.5 + 1}rem` }}
                    title={col.disabled ? 'Would exceed maximum nesting depth' : undefined}
                  >
                    {col.name}
                    {col.disabled && (
                      <span className="ml-2 text-xs text-foreground-muted">(Too deep)</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-4">
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                closeCollectionSidebar();
              }
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isPending || !formData.name.trim()}>
            {isPending ? (isEditMode ? 'Updating...' : 'Creating...') : isEditMode ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
  );
}
